import {
  TCreateProductReqDto,
  TCreateProductResDto,
  TDeleteProductsReqDto,
  TGetProductDetailResDto,
  TGetProductsResDto,
  TProduct,
  TToggleProductActiveReqDto,
  TUpdateProductReqDto,
  TUpdateProductResDto,
  TProductDetail,
} from "@/api/services/main/organization/product/dtos/product.dto";
import { clientApi } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";

/**
 * 商品設定 (product) API config for the account-backend main API.
 *
 *   GET    /organization/products                     -> list
 *   GET    /organization/products/:id                 -> detail
 *   POST   /organization/products                     -> create (custom product)
 *   PUT    /organization/products/:id                 -> update
 *   DELETE /organization/products                     -> bulk delete { ids }
 *   PATCH  /organization/products/:id/active          -> toggle 商品設定
 *
 * These functions are the single source of truth for the request/response shapes the FE sends.
 * (条件テンプレート was deferred — no template endpoints.)
 */

const BASE = "/organization/products";

export const fetchProductsApi = async (): Promise<TGetProductsResDto> => {
  const { data } = await clientApi.get<TGetProductsResDto>(BASE);
  return data;
};

export const fetchProductDetailApi = async (id: number): Promise<TGetProductDetailResDto> => {
  const { data } = await clientApi.get<TGetProductDetailResDto>(`${BASE}/${id}`);
  return data;
};

export const createProductApi = async (payload: TCreateProductReqDto): Promise<TCreateProductResDto> => {
  const { data } = await clientApi.post<TCreateProductResDto>(BASE, payload);
  return data;
};

export const updateProductApi = async (id: number, payload: TUpdateProductReqDto): Promise<TUpdateProductResDto> => {
  const { data } = await clientApi.put<TUpdateProductResDto>(`${BASE}/${id}`, payload);
  return data;
};

export const deleteProductsApi = async (payload: TDeleteProductsReqDto): Promise<void> => {
  await clientApi.delete(BASE, { data: payload });
};

export const toggleProductActiveApi = async (id: number, payload: TToggleProductActiveReqDto): Promise<void> => {
  await clientApi.patch(`${BASE}/${id}/active`, payload);
};

/** 商品一覧 fetch — GET /organization/products. */
export const useProductSettings = () => {
  const query = useQuery<TProduct[]>({
    queryKey: [QUERY_KEYS.PRODUCT.FETCH_LIST],
    queryFn: async () => (await fetchProductsApi()).data,
  });

  return { ...query, products: query.data ?? [] };
};

/** 商品詳細 fetch — GET /organization/products/:id (see fetchProductDetailApi). */
export const useProductDetail = (id?: number) => {
  const query = useQuery<TProductDetail | undefined>({
    queryKey: [QUERY_KEYS.PRODUCT.FETCH_DETAIL, id],
    queryFn: async () => (id != null ? (await fetchProductDetailApi(id)).data : undefined),
    enabled: id != null,
  });

  return { ...query, product: query.data };
};
